import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Button, MenuItem, TextField, InputAdornment } from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CreateIcon from '@mui/icons-material/Create';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import ItemDialog from 'components/popups/ItemDialog';
import ItemService from 'services/ItemService';
import { saveAs } from 'file-saver';
import Info from 'components/utils/Alert/Info';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { InventoryContext } from 'Context/InventoryContext';
import { USER_PERMISSIONS } from 'const';
import CsvHelper from 'helpers/CsvHelper';
import ShopsHelper from 'helpers/ShopsHelper';
import ArrayHelper from 'helpers/ArrayHelper';
import { Paper, Tooltip, IconButton } from '@mui/material';
import { ITEM_TYPES, SUBSCRIPTION_TYPE } from 'const';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'const';
import BarcodePrintDialog from 'components/popups/BarcodePrintDialog';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useTranslation } from 'react-i18next';
import CsvExportPopup from 'components/popups/CsvExportPopup';

function ItemsList() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const history = useHistory();
  const {
    getProducts,
    getCategories,
    getItemPriceCategories,
    getItemAddonMappings,
    getChargeGroups,
    getCharges,
    getVariantLabels,
    getProductVariantMapping,
    getBaseProducts,
    getStockBatches,
    stockBatches,
    products: items,
    categories,
  } = useContext(InventoryContext);

  const [filteredItems, setFilteredItems] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterText, setFilterText] = useState('');
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
  const [isShowAddItemDialog, setIsShowAddItemDialog] = useState(false);
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [filterItemType, setFilterItemType] = useState('all');
  const [deleteItem, setDeleteItem] = useState(null);
  const [printProductBarcode, setPrintProductBarcode] = useState(null);
  const [isShowExportCsvPopup, setIsShowExportCsvPopup] = useState(false);
  const [exportCsvColumns, setExportCsvColumns] = useState({});
  const [selectedColorFilters, setSelectedColorFilters] = useState([]);

  const buildVariantMappings = useCallback((item, productVariantMappingArg, variantLabelsArg, baseProductsArg) => {
    if (!item) return [];
    const mappingsForItem = (productVariantMappingArg || []).filter((m) => m.item === item.id);
    return mappingsForItem.map((m) => {
      const label = (variantLabelsArg || []).find((l) => l.id === m.label);
      const product = (baseProductsArg || []).find((p) => p.id === m.product);
      return {
        id: m.id,
        item: m.item,
        label: label
          ? {
              id: label.id,
              name: label.name,
              dimension: label.dimension ? { id: label.dimension.id, name: label.dimension.name } : null,
            }
          : null,
        product: product ? { id: product.id, name: product.name } : null,
      };
    });
  }, []);

  const buildPriceCategories = useCallback((item, itemPriceCategoriesArg) => {
    if (!item) return [];
    return (itemPriceCategoriesArg || []).filter((pc) => pc.item === item.id);
  }, []);

  const buildAddonMappings = useCallback((item, itemAddonMappingsArg, itemsArg) => {
    if (!item) return [];
    return (itemAddonMappingsArg || [])
      .filter((am) => am.item === item.id)
      .map((am) => {
        const addonItem = (itemsArg || []).find((it) => it.id === am.addon_item);
        return {
          ...am,
          addon_item: addonItem
            ? {
                id: addonItem.id,
                name: addonItem.name,
                plu: addonItem.plu,
              }
            : null,
        };
      });
  }, []);

  const buildChargeGroup = useCallback((item, chargeGroupsArg, chargesArg) => {
    if (!item) return '';

    const charge_group_data = item.charge_groups || item.charge_group;
    if (!charge_group_data) return '';

    const chargeGroupIds = Array.isArray(charge_group_data)
      ? charge_group_data
      : typeof charge_group_data === 'string'
      ? charge_group_data
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [charge_group_data];

    const groups = (chargeGroupsArg || []).filter(
      (g) =>
        chargeGroupIds.includes(g.id) || chargeGroupIds.includes(String(g.id)) || chargeGroupIds.includes(Number(g.id))
    );

    if (groups.length === 0) return '';

    return groups.map((g) => g.name).join(', ');
  }, []);

  const getCsvColumns = (
    categoriesArg,
    chargeGroupsArg,
    chargesArg,
    productVariantMappingArg,
    variantLabelsArg,
    baseProductsArg,
    itemAddonMappingsArg,
    items,
    itemPriceCategoriesArg
  ) => ({
    plu: { title: 'plu', exportTitle: 'PLU' },
    name: { title: 'name', exportTitle: 'Name' },
    unit: { title: 'unit', exportTitle: 'Unit' },
    category_name: {
      title: 'category',
      exportTitle: 'Category',
      getValue: (item) => categoriesArg?.find((category) => category.id === item.category)?.name,
    },
    type: { title: 'type', exportTitle: 'Type' },
    price: { title: 'price', exportTitle: 'Price' },
    mrp: { title: 'mrp', exportTitle: 'MRP' },
    cost: { title: 'cost', exportTitle: 'Cost' },
    tax: { title: 'tax', exportTitle: 'Tax' },
    cess: { title: 'cess', exportTitle: 'Cess' },
    stock: { title: 'stock', exportTitle: 'Stock' },
    hsn: { title: 'hsn', exportTitle: 'HSN' },
    ean: { title: 'ean', exportTitle: 'EAN' },
    order: { title: 'order', exportTitle: 'Order' },
    kg_ratio: { title: 'USP_Kg_Ratio', exportTitle: 'USP_Kg_Ratio' },
    is_available: {
      title: 'available',
      exportTitle: 'Available',
      getValue: (item) => (item.is_available ? 'YES' : 'NO'),
    },
    sync_enabled: {
      title: 'machine_sync',
      exportTitle: 'Machine_Sync',
      getValue: (item) => (item.sync_enabled ? 'YES' : 'NO'),
    },
    is_available_on_cart: {
      title: 'available_on_cart',
      exportTitle: 'Available_On_Cart',
      getValue: (item) => (item.is_available_on_cart ? 'YES' : 'NO'),
    },
    image: {
      title: 'Image',
      exportTitle: 'Image',
      getValue: (item) =>
        item.image && item.image.includes('%25') ? decodeURIComponent(item.image) : item.image || '',
    },
    external_item_id: { title: 'External_Item_ID', exportTitle: 'External_Item_ID' },
    available_days: { title: 'Available Days', exportTitle: 'Available Days' },
    available_time_range: { title: 'Available Time', exportTitle: 'Available Time' },
    config: {
      title: 'Config',
      exportTitle: 'Config',
      getValue: (item) => (item.config ? item.config : null),
    },
    details: {
      title: 'Details',
      exportTitle: 'Details',
      getValue: (item) => {
        if (!item.details) return null;
        let detailsObj = item.details;
        if (typeof detailsObj === 'string') {
          try {
            detailsObj = JSON.parse(detailsObj);
          } catch (e) {
            return item.details;
          }
        }
        if (detailsObj && Array.isArray(detailsObj.sub_items)) {
          const updatedSubItems = detailsObj.sub_items.map((si) => {
            const subItemObj = items?.find((it) => it.id === si.item_id);
            if (subItemObj) {
              return {
                ...si,
                ...(subItemObj.plu ? { plu: subItemObj.plu } : {}),
                ...(subItemObj.name ? { name: subItemObj.name } : {}),
              };
            }
            return si;
          });
          return {
            ...detailsObj,
            sub_items: updatedSubItems,
          };
        }
        return item.details;
      },
    },
    low_stock_alert_quantity: { title: 'low_stock_alert_quantity', exportTitle: 'Low Stock Alert Quantity' },
    expiry_alert_duration_in_hours: {title: 'expiry_alert_duration_in_hours',exportTitle: 'Expiry Alert Duration In Hours' },
    shelf_duration_in_hours: { title: 'shelf_duration_in_hours', exportTitle: 'Shelf Duration In Hours' },
    is_batch_enabled: {
      title: 'is_batch_enabled',
      exportTitle: 'Is Batch Enabled',
      getValue: (item) => (item.is_batch_enabled ? 'YES' : 'NO'),
    },
    price_category: {
      title: 'Price_Categories',
      exportTitle: 'Price_Categories',
      getValue: (item) => buildPriceCategories(item, itemPriceCategoriesArg),
    },
    charge_group: {
      title: 'Charge_Group',
      exportTitle: 'Charge Groups',
      getValue: (item) => buildChargeGroup(item, chargeGroupsArg, chargesArg),
    },
    variant_mappings: {
      title: 'Variant_Mappings',
      exportTitle: 'Variant_Mappings',
      getValue: (item) => buildVariantMappings(item, productVariantMappingArg, variantLabelsArg, baseProductsArg),
    },
    addon_mapping: {
      title: 'addon_items',
      exportTitle: 'Item_Addon_Mappings',
      getValue: (item) => buildAddonMappings(item, itemAddonMappingsArg, items),
    },
  });

  const dismissEditDialog = () => {
    setSelectedItemForEdit(null);
  };

  const dismissCreateDialog = () => {
    setIsShowAddItemDialog(false);
  };

  const showCreateDialog = () => {
    const isFreePlan = shop?.subscription?.type === SUBSCRIPTION_TYPE.FREE;
    if (isFreePlan && (items?.length || 0) >= 100) {
      postAlertMessage({
        text: 'Product limit reached for Free Plan (max 100 products). Please upgrade your plan to add more items.',
        type: 'failed',
        timeout: 5000,
      });
      return;
    }
    setIsShowAddItemDialog(true);
  };

  const handleViewStockChange = useCallback(
    (item) => {
      if (!item || !item.name) return;
      const query = new URLSearchParams({ itemName: item.name }).toString();
      history.push(`${ROUTES.STOCK_CHANGE_REPORT}?${query}`);
    },
    [history]
  );

  const handleViewItemDetails = useCallback(
    (item) => {
      if (!item || !item.id) return;
      history.push(`${ROUTES.ITEM_DETAILS.replace(':itemId', item.id)}`);
    },
    [history]
  );

  const handleExportScale = async () => {
    setIsShowLoading(true);
    try {
      await ItemService.updatePlu();
      postAlertMessage({
        text: tManagement('Item scale updated successfully'),
        type: 'success',
      });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setIsShowLoading(false);
  };

  const handleGetExportData = async () => {
    setIsShowLoading(true);
    try {
      const [
        baseProductsArg,
        categoriesArg,
        itemPriceCategoriesArg,
        itemAddonMappingsArg,
        chargeGroupsArg,
        chargesArg,
        variantLabelsArg,
        productVariantMappingArg,
      ] = await Promise.all([
        getBaseProducts(),
        getCategories(),
        getItemPriceCategories(),
        getItemAddonMappings(),
        getChargeGroups(),
        getCharges(),
        getVariantLabels(),
        getProductVariantMapping(),
      ]);
      const csvColumns = getCsvColumns(
        categoriesArg,
        chargeGroupsArg,
        chargesArg,
        productVariantMappingArg,
        variantLabelsArg,
        baseProductsArg,
        itemAddonMappingsArg,
        items,
        itemPriceCategoriesArg
      );
      setExportCsvColumns(csvColumns);
      setIsShowExportCsvPopup(true);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setIsShowLoading(false);
  };

  const handleExportCsv = async (items, csvColumns) => {
    const csv = CsvHelper.getString(items, csvColumns);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `${shop.shop_name}-products-${dateTime}.csv`);

    postAlertMessage({
      text: tManagement('Exported to excel successfully'),
      type: 'success',
    });
  };

  const handleImportCsv = async (csvFile) => {
    setIsShowLoading(true);

    try {
      const csvData = await CsvHelper.parseCsvFile(csvFile);
      const safeDecode = (str) => {
        try {
          return str ? decodeURIComponent(str) : str;
        } catch (e) {
          return str;
        }
      };
      const decodeImageUrl = (url) => {
        try {
          if (!url) return url;
          // Only decode if it contains %25 (double encoding indicator)
          return url.includes('%25') ? decodeURIComponent(url) : url;
        } catch (e) {
          return url;
        }
      };
      const importedItems = csvData.map((row, index) => {
        const plu = row.plu;
        const name = safeDecode(row.name);
        const price = row.price;
        const unit = safeDecode(row.unit);
        const categoryName = safeDecode(row.category);
        const type = row.type;

        if (!plu || plu === '0') {
          throw new Error('PLU must be provided and cannot be zero.');
        } else if (!name || !price || !unit || !categoryName || !type) {
          throw new Error(`On ${index + 1}th row, name, unit, price, category or type is empty`);
        }

        const eanVal = String(row.ean || '').trim();
        if (eanVal && eanVal !== 'nil' && !/^[a-zA-Z0-9]+$/.test(eanVal)) {
          throw new Error(
            `On ${index + 1}th row, EAN "${eanVal}" contains invalid characters. Please use only letters and numbers.`
          );
        }
        const category = categories?.find((c) => c.name === categoryName) || {};
        const isYes = (val) => typeof val === 'string' && val.toUpperCase() === 'YES';

        const existingItem = items?.find((it) => String(it.plu).trim() === String(plu).trim());

        return {
          id: existingItem?.id,
          plu: Number(plu),
          name: name.trim(),
          unit: unit.trim(),
          category: { id: category.id, name: categoryName },
          type: String(row.type || 'SERVICE')
            .trim()
            .toUpperCase(),
          price: Number(ShopsHelper.getAmountFormatted(shop, price)),
          mrp: Number(ShopsHelper.getAmountFormatted(shop, row.mrp || price || 0)),
          cost: Number(ShopsHelper.getAmountFormatted(shop, row.cost || 0)),
          tax: Number(row.tax || 0),
          cess: Number(row.cess || row.additionaltax || row.additionaltaxcess || 0),
          stock: Number(row.stock || 0),
          hsn: Number(row.hsn || 0),
          ean: String(row.ean || '').trim() || 'nil',
          order: row.order !== null && row.order !== undefined && row.order !== '' ? Number(row.order) : null,
          is_available: row.available && row.available.trim() !== '' ? true : isYes(row.available),
          sync_enabled: isYes(row.machinesync),
          is_available_on_cart:
            row.availableoncart && row.availableoncart.trim() !== '' ? true : isYes(row.availableoncart),
          kg_ratio: row.uspkgratio ? Number(row.uspkgratio) : null,
          image: decodeImageUrl(row.image) || '',
          external_item_id: String(row.externalitemid || '').trim(),
          config: row.config || null,
          details: row.details || null,
          low_stock_alert_quantity:
            row.lowstockalertquantity !== undefined
              ? row.lowstockalertquantity !== null && row.lowstockalertquantity !== ''
                ? Number(row.lowstockalertquantity)
                : null
              : existingItem?.low_stock_alert_quantity ?? null,
          expiry_alert_duration_in_hours:
            row.expiryalertdurationinhours !== undefined
              ? row.expiryalertdurationinhours !== null && row.expiryalertdurationinhours !== ''
                ? Number(row.expiryalertdurationinhours)
                : null
              : existingItem?.expiry_alert_duration_in_hours ?? null,
          shelf_duration_in_hours:
            row.shelfdurationinhours !== undefined
              ? row.shelfdurationinhours !== null && row.shelfdurationinhours !== ''
                ? Number(row.shelfdurationinhours)
                : null
              : existingItem?.shelf_duration_in_hours ?? null,
          is_batch_enabled:
            row.isbatchenabled !== undefined ? isYes(row.isbatchenabled) : existingItem?.is_batch_enabled ?? false,
          charge_groups: row.chargegroup || null,
          item_addon_mappings: row.itemaddonmappings || null,
          variant_mappings: row.variantmappings || null,
          price_categories: row.pricecategories || null,
        };
      });

      // Check product limit for Free Plan
      const isFreePlan = shop?.subscription?.type === SUBSCRIPTION_TYPE.FREE;
      if (isFreePlan) {
        const newItemsCount = importedItems.filter((item) => !item.id).length;
        const totalCount = (items?.length || 0) + newItemsCount;
        if (totalCount > 100) {
          throw new Error('Importing these items would exceed the 100 product limit for the Free Plan.');
        }
      }

      const alertId = 'items-batch-update';
      const itemsChunked = ArrayHelper.chunk(importedItems, 40);
      postAlertMessage({
        key: alertId,
        text: `Updating items as ${itemsChunked.length} segments`,
        timeout: -1,
      });
      for (const [index, itemsChunk] of itemsChunked.entries()) {
        await ItemService.updateItems(itemsChunk);
        postAlertMessage({
          key: alertId,
          text: `${index + 1}th chunk uploaded successfully`,
          timeout: -1,
        });
      }

      fetchProducts();

      postAlertMessage({
        key: alertId,
        text: tManagement('Item imported successfully'),
        type: 'success',
      });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed', timeout: 5000 });
    }
    setIsShowLoading(false);
  };

  const handleDeleteClick = (data) => setDeleteItem(data);

  const handleDeleteItem = async () => {
    setIsShowLoading(true);
    try {
      await ItemService.deleteItem(deleteItem.id);
      postAlertMessage({
        text: tManagement('Item deleted successfully'),
        type: 'success',
      });
      fetchProducts();
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setDeleteItem(null);
      setIsShowLoading(false);
    }
  };

  const batchStatusMap = useMemo(() => {
    const statusMap = {};
    if (shop?.subscription?.type !== SUBSCRIPTION_TYPE.PREMIUM || !stockBatches.length) return statusMap;
    const now = new Date();
    const itemsMap = (items || []).reduce((acc, it) => {
      acc[it.id] = it;
      return acc;
    }, {});

    stockBatches.forEach((batch) => {
      if (!batch.item || !batch.expiry_date || batch.stock <= 0) return;

      const item = itemsMap[batch.item];
      if (!item) return;

      const expiryDate = new Date(batch.expiry_date.endsWith('Z') ? batch.expiry_date.slice(0, -1) : batch.expiry_date);
      const diffInHours = (expiryDate - now) / (1000 * 60 * 60);

      if (!statusMap[batch.item]) {
        statusMap[batch.item] = { hasExpired: false, hasGoingToExpire: false };
      }

      if (diffInHours <= 0) {
        statusMap[batch.item].hasExpired = true;
      } else if (item.expiry_alert_duration_in_hours && diffInHours <= Number(item.expiry_alert_duration_in_hours)) {
        statusMap[batch.item].hasGoingToExpire = true;
      }
    });
    return statusMap;
  }, [shop, stockBatches, items]);

  const headerData = useMemo(() => {
    let headers = [
      {
        label: tCommon('PLU'),
        id: 'plu',
        type: 'callback',
        style: { width: '70px', minWidth: '70px' },
        viewRender: (row) => {
          const hasExpired = batchStatusMap[row.id]?.hasExpired;
          const hasGoingToExpire = batchStatusMap[row.id]?.hasGoingToExpire;
          const isLowStock =
            row.low_stock_alert_quantity !== null &&
            row.low_stock_alert_quantity !== undefined &&
            row.low_stock_alert_quantity !== '' &&
            Number(row.stock) < Number(row.low_stock_alert_quantity);
            const isOutOfStock = row.type !== ITEM_TYPES.SERVICE && Number(row.stock) <= 0;

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center', width: '12px', minHeight: '1px' }}>
                {hasExpired && (
                  <Tooltip title={tCommon('Expired Batch')} arrow>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                  </Tooltip>
                )}
                {hasGoingToExpire && (
                  <Tooltip title={tCommon('Going to Expire Batch')} arrow>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#f97316' }} />
                  </Tooltip>
                )}
                {isLowStock && (
                  <Tooltip title={tCommon('Low Stock Alert')} arrow>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3b82f6' }} />
                  </Tooltip>
                )}
                {isOutOfStock && (
                  <Tooltip title={tCommon('Out of Stock')} arrow>
                    <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#a855f7' }} />
                  </Tooltip>
                )}
              </div>
              <span>{row.plu}</span>
            </div>
          );
        },
      },
      {
        label: tCommon('Name'),
        id: 'name',
        type: 'text',
        main: 'true',
        style: { minWidth: '120px' },
      },
      {
        label: tCommon('Unit'),
        id: 'unit',
        type: 'text',
        style: { width: '45px', minWidth: '45px' },
      },
      {
        label: tCommon('Price'),
        id: 'price',
        type: 'callback',
        style: { width: '65px', minWidth: '65px' },
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.price),
      },
      {
        label: tCommon('MRP'),
        id: 'mrp',
        type: 'callback',
        style: { width: '65px', minWidth: '65px' },
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.mrp),
      },
      {
        label: tCommon('Tax(%)'),
        id: 'tax',
        type: 'text',
        style: { width: '55px', minWidth: '55px' },
      },
      {
        label: tCommon('Additional Tax(%)'),
        id: 'cess',
        type: 'text',
        style: { width: '95px', minWidth: '95px' },
      },
      {
        label: tCommon('Cost'),
        id: 'cost',
        type: 'callback',
        style: { width: '65px', minWidth: '65px' },
        viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.cost),
      },
      {
        label: tCommon('Type'),
        id: 'type',
        type: 'text',
        style: { width: '90px', minWidth: '90px' },
      },
      {
        label: tCommon('Available'),
        id: 'is_available',
        type: 'callback',
        style: { width: '70px', minWidth: '70px' },
        viewRender: (item) => {
          return item.is_available ? 'Yes' : 'No';
        },
      },
      {
        label: tCommon('Order'),
        id: 'order',
        type: 'text',
        style: { width: '50px', minWidth: '50px' },
      },
      {
        label: tCommon('Stock'),
        id: 'stock',
        type: 'callback',
        style: { width: '60px', minWidth: '60px' },
        viewRender: (item) => {
          if (item.type === ITEM_TYPES.SERVICE || item.type === ITEM_TYPES.COMBO) {
            return '-';
          }
          return item.stock;
        },
      },
    ];
    if (shop && shop.image_support_validity != null) {
      headers.splice(2, 0, {
        label: tCommon('Image'),
        id: 'image',
        type: 'image',
        style: { width: '55px', minWidth: '55px' },
      });
    }
    if (((user || {}).shop_permissions || {}).products_permission === USER_PERMISSIONS.WRITE) {
      headers.push({
        label: tCommon('Actions'),
        id: 'updateItems',
        title: 'Edit Item',
        type: 'callback',
        style: { width: '160px', minWidth: '160px' },
        viewRender: (item) => {
          return (
            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
              <Tooltip
                title="Print Barcode"
                onClick={() => {
                  setPrintProductBarcode(item);
                }}
              >
                <IconButton size="small" style={{ padding: '4px' }}>
                  <QrCodeIcon className={styles.iconButton} fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={tManagement('View Item Details')} onClick={() => handleViewItemDetails(item)}>
                <span>
                  <IconButton size="small" style={{ padding: '4px' }}>
                    <RemoveRedEyeIcon className={styles.iconButton} fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={tManagement('View Stock Change')} onClick={() => handleViewStockChange(item)}>
                <span>
                  <IconButton size="small" style={{ padding: '4px' }}>
                    <AssessmentIcon className={styles.iconButton} fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={tManagement('Edit Item')} onClick={() => setSelectedItemForEdit(item)}>
                <IconButton size="small" style={{ padding: '4px' }}>
                  <EditIcon className={styles.iconButton} fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={tManagement('Delete Item')} onClick={() => handleDeleteClick(item)}>
                <IconButton size="small" style={{ padding: '4px' }}>
                  <DeleteIcon className={styles.iconButton} fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      });
    }
    return headers;
  }, [shop, user, handleViewStockChange, tCommon, tManagement, handleViewItemDetails, batchStatusMap]);



  const getRowStyle = (row) => {
    return {};
  };

  const fetchProducts = async () => {
    const items = await getProducts({ useCache: false });
    setFilteredItems(items);
  };

  const fetchFilteredItems = useCallback(async () => {
    if (!items) {
      setFilteredItems([]);
      return;
    }
    let filteredProducts = items;

    if (filterText) {
      const searchText = filterText.toLowerCase();

      filteredProducts = filteredProducts.filter((item) => {
        const name = item.name?.toLowerCase() || '';
        const plu = item.plu?.toString().toLowerCase() || '';
        const ean = item.ean?.toString().toLowerCase() || '';

        return name.includes(searchText) || plu.includes(searchText) || ean.includes(searchText);
      });
    }

    if (filterCategory && filterCategory !== 'all') {
      filteredProducts = filteredProducts.filter((item) => item.category === filterCategory);
    }

    if (filterItemType && filterItemType !== 'all') {
      filteredProducts = filteredProducts.filter((item) => item.type === filterItemType);
    }

    if (selectedColorFilters.length > 0) {
      filteredProducts = filteredProducts.filter((item) => {
        const hasExpired = batchStatusMap[item.id]?.hasExpired;
        const hasGoingToExpire = batchStatusMap[item.id]?.hasGoingToExpire;
        const isLowStock =
          item.low_stock_alert_quantity !== null &&
          item.low_stock_alert_quantity !== undefined &&
          item.low_stock_alert_quantity !== '' &&
          Number(item.stock) < Number(item.low_stock_alert_quantity);
        const isOutOfStock = item.type !== ITEM_TYPES.SERVICE && Number(item.stock) <= 0;

        return selectedColorFilters.every((filter) => {
          if (filter === 'expired') return hasExpired;
          if (filter === 'going_to_expire') return hasGoingToExpire;
          if (filter === 'low_stock') return isLowStock;
          if (filter === 'out_of_stock') return isOutOfStock;
          return false;
        });
      });
    }

    setFilteredItems(filteredProducts);
  }, [items, filterText, filterCategory, filterItemType, selectedColorFilters, batchStatusMap]);

  useEffect(() => {
    fetchFilteredItems();
  }, [fetchFilteredItems]);

  useEffect(() => {
    getProducts();
  }, [getProducts]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  useEffect(() => {
    if (shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM) {
      getStockBatches();
    }
  }, [shop, getStockBatches]);

  return (
    <div className={styles.contentWrapperSm}>
      <ConfirmPopup
        data={deleteItem}
        handleClose={handleDeleteClick}
        handleOkay={handleDeleteItem}
        alertTitle={tCommon('Confirm Delete')}
        successBtnName={tCommon('Delete')}
        alertContent={tManagement('Are you sure you want to delete this item?')}
      />
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {' '}
          {tCommon('Products')}
          <span className={styles.menuTitle}>{tCommon('list')}</span>
        </h2>
      </div>
      <div className={styles.changeable}>
        <div className={styles.toolbarRow}>
          {/* 1. Search */}
          <TextField
            variant="outlined"
            size="small"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder={tCommon('Search items')}
            label={tCommon('Search')}
            className={`${styles.filterInput} ${styles.toolbarItem}`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon style={{ color: 'gray' }} />
                </InputAdornment>
              ),
            }}
          />

          {/* 2. Filters */}
          <TextField
            select
            variant="outlined"
            size="small"
            value={filterItemType}
            onChange={(e) => setFilterItemType(e.target.value)}
            className={`${styles.selectBox} ${styles.toolbarItem}`}
            label={tCommon('Item Type')}
          >
            <MenuItem value="all">{tCommon('All Types')}</MenuItem>
            <MenuItem value={ITEM_TYPES.INVENTORY}>{tCommon('Inventory')}</MenuItem>
            <MenuItem value={ITEM_TYPES.COMBO}>{tCommon('Combo')}</MenuItem>
            <MenuItem value={ITEM_TYPES.SERVICE}>{tCommon('Service')}</MenuItem>
            <MenuItem value={ITEM_TYPES.PRODUCTION}>{tCommon('Production')}</MenuItem>
          </TextField>

          <TextField
            select
            variant="outlined"
            size="small"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={`${styles.selectBox} ${styles.toolbarItem}`}
            label={tCommon('Item Category')}
          >
            <MenuItem value="all">{tCommon('All')}</MenuItem>
            {categories?.map((category) => (
              <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
            ))}
            <MenuItem value="none">None</MenuItem>
          </TextField>

          {/* 3. Buttons */}
          {((user || {}).shop_permissions || {}).products_permission !== USER_PERMISSIONS.READ && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={`${styles.actionBtn} ${styles.toolbarItem}`}
                onClick={showCreateDialog}
              >
                <CreateIcon className={styles.actionBtnIcon} />
                {tManagement('New Item')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={`${styles.actionBtn} ${styles.toolbarItem}`}
                style={{ backgroundColor: '#ff851b', padding: 0 }}
              >
                <input
                  accept="text/csv"
                  type="file"
                  hidden
                  id="import"
                  onChange={(event) => {
                    const fileField = event.target;
                    const csvFile = fileField.files[0];
                    fileField.value = null;
                    handleImportCsv(csvFile);
                  }}
                />
                <label htmlFor="import" style={{ cursor: 'pointer', margin: 0, width: '100%', height: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <ImportExportIcon className={styles.actionBtnIcon} style={{ margin: 0 }} />
                  {tCommon('Import CSV')}
                </label>
              </Button>
            </>
          )}

          {shop?.subscription?.type !== SUBSCRIPTION_TYPE.FREE && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                className={`${styles.actionBtn} ${styles.toolbarItem}`}
                style={{ backgroundColor: '#605ca8' }}
                onClick={handleExportScale}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('Export to Scale')}
              </Button>

              <Button
                variant="contained"
                color="primary"
                size="small"
                className={`${styles.actionBtn} ${styles.toolbarItem}`}
                style={{ backgroundColor: '#d81b60' }}
                onClick={handleGetExportData}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('Export to CSV')}
              </Button>
            </>
          )}
        </div>

        {/* Color Indication Legend */}
        <div className={styles.legendContainer} style={{ alignItems: 'center' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginRight: '5px' }}>{tCommon('Legend:')}</span>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              opacity: selectedColorFilters.length === 0 ? 1 : 0.4,
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: selectedColorFilters.length === 0 ? '#e2e8f0' : 'transparent',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setSelectedColorFilters([])}
            title={tCommon('Show All Items')}
          >
            <span style={{ fontSize: '12px', fontWeight: selectedColorFilters.length === 0 ? 600 : 500, color: '#333' }}>{tCommon('All')}</span>
          </div>

          {[
            { key: 'expired', label: tCommon('Expired Batch'), color: '#ef4444' },
            { key: 'going_to_expire', label: tCommon('Going to Expire Batch'), color: '#f97316' },
            { key: 'low_stock', label: tCommon('Low Stock Alert'), color: '#3b82f6' },
            { key: 'out_of_stock', label: tCommon('Out of Stock'), color: '#a855f7' }
          ].map((item) => (
            <div
              key={item.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                opacity: selectedColorFilters.length === 0 || selectedColorFilters.includes(item.key) ? 1 : 0.4,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: selectedColorFilters.includes(item.key) ? `${item.color}15` : 'transparent',
                transition: 'all 0.2s ease'
              }}
              onClick={() => {
                setSelectedColorFilters((prev) =>
                  prev.includes(item.key) ? prev.filter((k) => k !== item.key) : [...prev, item.key]
                );
              }}
              title={`Filter by ${item.label}`}
            >
              <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  display: 'inline-block',
                }}
              />
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: selectedColorFilters.includes(item.key) ? 600 : 500,
                  color: '#333',
                }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {(filteredItems || isShowLoading || items === null || categories === null) &&
        ((filteredItems && filteredItems.length) || isShowLoading || items === null || categories === null ? (
          <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: '4px' }}>
            <DataTable
              columns={headerData}
              rows={filteredItems || []}
              isLoading={isShowLoading || items === null || categories === null}
              rowStyle={getRowStyle}
              pagination={true}
              rowsPerPageOptions={[20, 50, 100]}
              tableStyle={{ minWidth: '980px' }}
            />
          </Paper>
        ) : (
          !isShowLoading &&
          items !== null && (
            <Info
              title={tManagement('You have no item to current filter')}
              content={tManagement(
                'There are no results that match your current filter configration. Please try a different filter combination to view the desired data.'
              )}
            />
          )
        ))}

      {(selectedItemForEdit || isShowAddItemDialog) && (
        <ItemDialog
          handleClose={selectedItemForEdit ? dismissEditDialog : dismissCreateDialog}
          item={selectedItemForEdit}
          itemCount={items?.length || 0}
        />
      )}
      {printProductBarcode && (
        <BarcodePrintDialog
          handleClose={() => {
            setPrintProductBarcode(null);
          }}
          items={[printProductBarcode]}
        />
      )}
      {isShowExportCsvPopup && (
        <CsvExportPopup
          title={tCommon('Export to CSV')}
          handleClose={() => setIsShowExportCsvPopup(false)}
          onExport={(selectedColumns) => handleExportCsv(items, selectedColumns)}
          columnMapping={exportCsvColumns}
        />
      )}
    </div>
  );
}

export default withConsoleBase(ItemsList);
